import { Builder } from 'xml2js';
import { Dispatch } from 'redux';
import { setError } from 'pages/store/actions/employeeShops';
import { EmployeeFromApi } from '../types/employeeShops';
import { Shop } from '../types/shops';
import { getIsValidEmployeeID } from './employee';
import trackError from './trackError';

interface FetchEmployeeShopsInput {
  employeeID: string;
}

const fetchEmployeeShops = async ({
  employeeID,
}: FetchEmployeeShopsInput): Promise<EmployeeFromApi> => {
  if (!getIsValidEmployeeID(employeeID)) {
    throw new Error('Invalid employee ID');
  }

  // Cannot use loadRelations param because it prepends a '&' instead of '?'
  const response = await window.merchantos.session.apiRequest(
    `API/Account/{{systemCustomerID}}/Employee/${employeeID}.json?load_relations=["EmployeeShops"]`
  );

  return response;
};

const getEmployeeShopsIds = (jsonResponse: TSFixMe) => {
  const employeeShop = jsonResponse?.Employee?.EmployeeShops?.EmployeeShop || [];

  // Single entity is an object; multiple entities are an array
  const employeeShopArray = [].concat(employeeShop);

  // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
  return employeeShopArray.reduce((prev, curr) => {
    const { shopID } = curr;
    if (!shopID) {
      return prev;
    }
    return [...prev, parseInt(shopID, 10)];
  }, []);
};

export const fetchAndGetEmployeeShopIDs = async ({
  employeeID,
}: FetchEmployeeShopsInput): Promise<number[]> => {
  try {
    const jsonResponse = await fetchEmployeeShops({ employeeID });
    return getEmployeeShopsIds(jsonResponse);
  } catch (error) {
    trackError({ description: 'fetchAndGetEmployeeShopIDs failed', error });
    return [];
  }
};

const createEmployeeShopsPayload = (employeeShops: Shop[]): string => {
  const preparedShops = employeeShops.map(shop => ({
    EmployeeShop: {
      shopID: shop.id,
    },
  }));
  const jsonOutput = {
    Employee: {
      EmployeeShops: [preparedShops],
    },
  };
  const xmlBuilder = new Builder();
  return xmlBuilder.buildObject(jsonOutput);
};

interface PutEmployeeShops {
  employeeID: string;
  employeeShops: Shop[];
}

export const putEmployeeShops = ({ employeeID, employeeShops }: PutEmployeeShops) => {
  const systemCustomerID = window.merchantos.session.getSystemCustomerID();

  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | number | null' is not a... Remove this comment to see the full error message
  if (!getIsValidEmployeeID(employeeID) || !getIsValidEmployeeID(systemCustomerID)) {
    throw new Error('Invalid employee ID or customer ID');
  }

  if (!employeeShops.length) {
    throw new Error('Invalid locations');
  }

  return window.fetch(`API/Account/${systemCustomerID}/Employee/${employeeID}`, {
    body: createEmployeeShopsPayload(employeeShops),
    headers: {
      'Content-Type': 'text/xml',
    },
    method: 'PUT',
  });
};

export const flashNotifierDelay = 3;

export const showPutEmployeeShopsSuccess = () =>
  window.flashNotifier?.('success', 'Saved Changes', flashNotifierDelay);

export const showEmployeeWithoutAccessWarning = () =>
  window.flashNotifier?.(
    'warning',
    'Employee left without access to any location',
    flashNotifierDelay
  );

type TrackPutEmployeeShopsErrorInput = {
  error: Error;
  flashNotifier?: boolean;
  dispatch?: Dispatch;
};

export const trackPutEmployeeShopsError = ({
  error,
  flashNotifier = false,
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'null' is not assignable to type 'Dispatch<An... Remove this comment to see the full error message
  dispatch = null,
}: TrackPutEmployeeShopsErrorInput) => {
  const errorTrace = {
    description: "Unable to save employee's shop access. Try again.",
    error,
    flashNotifier,
  };

  dispatch?.(setError(errorTrace));
  trackError(errorTrace);
};
